import {
  carriedEntries,
  SHEFIRA_ZONE_ID,
  TUTORIAL_ZONE_ID,
  BIOMES,
  biomeAt,
  getItem,
  type InventoryState,
  TILE_SIZE,
  type ZoneDefinition,
} from '@shefira/shared';

import { playSfx } from './sound.js';

/**
 * What the game sounds like, decided from what the server already said: the
 * music of the place the player stands in, and which effect an event rings.
 * Presentation only — nothing here changes the game, it listens to it.
 */

/**
 * Each effect's level against the others, kept here rather than in the files
 * (every file is peak-normalised): a swing heard a hundred times a fight sits
 * well under a level-up. Balanced on measured loudness (K-weighted, loudest
 * 100 ms, 2026-09-29): interface and chat about 6 dB under the blows, blows
 * a little over the music, spells together around them, rewards above, and
 * the level-up the loudest of all.
 */
const LEVELS: Record<string, number> = {
  'sfx.swing': 0.35,
  'sfx.shot': 0.4,
  'sfx.hit': 0.55,
  'sfx.crit': 0.7,
  'sfx.hurt': 0.6,
  'sfx.mob-death': 0.5,
  // The low-health heartbeat, every second or so: felt more than heard, under the blows.
  'sfx.heartbeat': 0.45,
  'sfx.death': 0.8,
  'sfx.level-up': 1,
  'sfx.spell-strike': 0.7,
  'sfx.spell-whirlwind': 0.52,
  'sfx.spell-war-cry': 0.5,
  'sfx.spell-charge': 0.72,
  'sfx.spell-rend': 0.7,
  'sfx.spell-shield-wall': 0.87,
  'sfx.spell-shield-bash': 0.76,
  'sfx.spell-taunt': 0.5,
  'sfx.spell-seismic': 0.45,
  'sfx.spell-rage': 0.6,
  'sfx.spell-execute': 0.7,
  'sfx.spell-titan': 0.88,
  // A boss's blows must carry over a fight's din: the warning is the point.
  'sfx.boss-telegraph': 0.7,
  'sfx.boss-impact': 0.85,
  'sfx.boss-roar': 0.8,
  // A world event's call (ADR 0050): heard across the valley, the whole server is called.
  'sfx.event-horn': 0.75,
  // A wandering rare (ADR 0094): told to its region only, under the horn, over the blows.
  'sfx.rare-call': 0.65,
  // Shefira (ADR 0067): the loudest thing in the game, heard twice in a journey.
  'sfx.dragon-roar': 0.95,
  'sfx.dragon-wings': 0.6,
  'sfx.dragon-rumble': 0.8,
  'sfx.coins': 0.32,
  'sfx.pickup': 0.45,
  'sfx.drop': 0.4,
  'sfx.loot-rare': 0.6,
  'sfx.equip': 0.5,
  'sfx.potion': 0.55,
  'sfx.upgrade-success': 0.63,
  'sfx.upgrade-fail': 0.58,
  'sfx.fuse': 0.54,
  'sfx.brew': 0.41,
  'sfx.teleport': 0.7,
  'sfx.portal': 0.6,
  'sfx.mount': 0.37,
  'sfx.click': 0.3,
  'sfx.open': 0.35,
  'sfx.close': 0.3,
  'sfx.error': 0.15,
  'sfx.chat': 0.15,
  'sfx.discover': 0.45,
};

/** Every effect: preloaded when the world opens, so the first of each is on time. */
export const SFX_KEYS = Object.keys(LEVELS);

/** An effect heard from the player's own place: the interface, their own blows. */
export function cue(key: string, volume = 1): void {
  playSfx(key, { volume: (LEVELS[key] ?? 0.5) * volume, rate: 0.97 + Math.random() * 0.06 });
}

/** Beyond this many tiles an event is not heard at all. */
const HEARING_TILES = 14;

/**
 * An effect somewhere in the world: quieter with the distance from the
 * listener (the local player, the camera's centre), and panned to its side.
 */
export function cueAt(
  key: string,
  at: { x: number; y: number },
  listener: { x: number; y: number } | undefined,
): void {
  if (listener === undefined) {
    cue(key);
    return;
  }
  const dx = (at.x - listener.x) / TILE_SIZE;
  const dy = (at.y - listener.y) / TILE_SIZE;
  const distance = Math.hypot(dx, dy);
  if (distance >= HEARING_TILES) return;
  const falloff = 1 - distance / HEARING_TILES;
  playSfx(key, {
    volume: (LEVELS[key] ?? 0.5) * falloff * falloff,
    pan: dx / 10,
    rate: 0.95 + Math.random() * 0.1,
  });
}

/** The capital (the meadow's village) has its own tune; the hamlets share theirs. */
const CAPITAL = BIOMES.meadow.village;

/**
 * The music of a tile: the boss's inside a dungeon, the village's while
 * inside one, the biome's otherwise. The same names the minimap gives, so the music changes where
 * the screen says « Tu entres ».
 */
export function musicFor(zone: ZoneDefinition, at: { x: number; y: number }): string {
  // The camp of the first steps (ADR 0030) is one small place: its rats'
  // clearing is steps from the square, and the tune must not change on the way.
  if (zone.id === TUTORIAL_ZONE_ID) return 'music.village';
  // Shefira's Aire (ADR 0068), an instance and never `persistent`: the last stand's own tune.
  if (zone.id === SHEFIRA_ZONE_ID) return 'music.boss-shefira';
  // A boss's dungeon (ADR 0027) has its own, in its biome's colours.
  if (zone.persistent) return `music.boss-${biomeAt(zone, at) ?? 'meadow'}`;
  const village = zone.landmarks.find(
    (landmark) =>
      landmark.kind === 'village' &&
      Math.hypot(landmark.x - at.x, landmark.y - at.y) <= landmark.radius,
  );
  if (village !== undefined) return village.name === CAPITAL ? 'music.capital' : 'music.village';
  const biome = biomeAt(zone, at);
  return `music.${biome ?? 'meadow'}`;
}

function counts(inventory: InventoryState): Map<string, number> {
  const byTemplate = new Map<string, number>();
  for (const { item } of carriedEntries(inventory)) {
    byTemplate.set(item.templateId, (byTemplate.get(item.templateId) ?? 0) + item.quantity);
  }
  return byTemplate;
}

/**
 * The sound of an inventory change, from the difference between the last
 * two the server sent: a piece put on or taken off, a potion drunk, gold or
 * an item gained. One sound per change, the most telling one.
 */
export function inventoryCue(previous: InventoryState, next: InventoryState): string | undefined {
  const worn = (inventory: InventoryState): string =>
    Object.values(inventory.equipped)
      .map((item) => item.id)
      .sort()
      .join();
  if (worn(previous) !== worn(next)) return 'sfx.equip';
  // Gold spent is heard by what bought it (the anvil, the crucible, the stall).
  if (next.gold > previous.gold) return 'sfx.coins';
  if (next.gold < previous.gold) return undefined;
  const before = counts(previous);
  const after = counts(next);
  for (const [templateId, quantity] of before) {
    const left = after.get(templateId) ?? 0;
    if (left < quantity && getItem(templateId)?.kind === 'consumable') return 'sfx.potion';
  }
  for (const [templateId, quantity] of after) {
    if (quantity > (before.get(templateId) ?? 0)) return 'sfx.pickup';
  }
  return undefined;
}
